import React, { type ComponentProps } from 'react'

import type { AppLinkProps } from '@/components/elementary/AppLink/types'
import {
	buildLinkClassName,
	determineLinkVariant,
	getAnchorHref,
	getLinkExceptions,
	renderContent,
} from '@/components/elementary/AppLink/utils'
import { Link } from '@/i18n/navigation'
import { isAppLink } from '@/utils/links'

const AppLinkComponent = React.forwardRef<HTMLAnchorElement, AppLinkProps>(
	(
		{
			href,
			label,
			className,
			children,
			iconEnd,
			iconStart,
			openExternalInNewTab = false,
			forceAnchor = false,
			size = 'none',
			inverted,
			underline = false,
			...props
		},
		ref,
	) => {
		const variant = determineLinkVariant(href, label || children?.toString(), props.variant)
		const linkClassName = buildLinkClassName({ variant, size, inverted, className, underline })

		const linkException = getLinkExceptions(
			{
				href,
				ref,
				linkClassName,
				props,
			},
			() => renderContent({ variant, iconStart, iconEnd, label, children }),
		)
		if (linkException) {
			return linkException
		}

		const isExternal = typeof href === 'string' && !isAppLink(href)
		if (isExternal || forceAnchor) {
			return (
				<a
					href={getAnchorHref(href, isExternal)}
					ref={ref}
					target={openExternalInNewTab ? '_blank' : ''}
					rel={openExternalInNewTab ? 'noopener noreferrer' : ''}
					className={linkClassName}
					{...props}
				>
					{renderContent({ variant, iconStart, iconEnd, label, children })}
				</a>
			)
		}

		return (
			<Link
				href={href as ComponentProps<typeof Link>['href']}
				ref={ref}
				className={linkClassName}
				{...props}
			>
				{renderContent({ variant, iconStart, iconEnd, label, children })}
			</Link>
		)
	},
)

const AppLinkSSRFallback = React.forwardRef<HTMLAnchorElement, AppLinkProps>((props, ref) => (
	<AppLinkComponent {...props} ref={ref} />
))

export default AppLinkSSRFallback
